import React, { Fragment, ReactNode } from "react";
import "./CustomTable.scss";
import { Table, Menu, Portal, Box } from "@chakra-ui/react";
import LeopartLoader from "../LeopartLoader/LeopartLoader";
import { objectToClassString } from "../../utils/functions";
import lodash from "lodash";
import { DotsThreeVerticalIcon } from "@phosphor-icons/react";
import classNames from "classnames";

interface OptionRecord<T extends any[]> {
  color?: "red";
  show?: (record: any) => boolean;
  icon?: "delete" | "info" | ReactNode;
  label: ReactNode;
  onclick: (record: T[number]) => void;
}

export interface ICustomTable<T extends any[], K extends string[]> {
  headers: K;
  data?: T;
  tableLayout?: "fixed" | "auto";
  className?: string;
  style?: React.CSSProperties;
  isLoading?: boolean;
  optionsRecord?: OptionRecord<T>[];
  disabledRecord?: (record: any) => boolean;
  fields: Record<
    string,
    {
      key: string;
      customComponent?: (value: any, record: T[number]) => ReactNode;
      customValue?: (value: any, record: T[number]) => string | number;
      className?: string;
      classNameTd?: string;
      colSpan?: number;
      isNumeric?: string;
      align?: "start" | "center" | "end";
    }
  >;
}

const CustomTable = <T extends any[] = any[], K extends string[] = string[]>({
  headers,
  data,
  tableLayout = "auto",
  optionsRecord,
  isLoading,
  disabledRecord,
  className = "",
  style,
  fields,
}: ICustomTable<T, K>) => {
  const renderHeader = (header: ICustomTable<T, K>["headers"][0]) => {
    return (
      <Table.ColumnHeader
        colSpan={fields[header]?.colSpan ?? 1}
        key={header}
        className="th"
      >
        <div className="separator__th"></div>
        {header}
      </Table.ColumnHeader>
    );
  };

  const renderValue = (
    record: T[number],
    header: ICustomTable<T, K>["headers"][number],
    index: number,
  ) => {
    if (!(header in fields)) return <Table.Cell className="td"></Table.Cell>;
    const field = fields[header];

    if (
      !lodash.has(record, field.key) &&
      !field.customValue &&
      !field.customComponent
    )
      return (
        <Table.Cell
          className={classNames("td", {
            disabled: !!disabledRecord?.(record),
            [field.classNameTd as string]: !!field.classNameTd,
          })}
          colSpan={field.colSpan}
        ></Table.Cell>
      );
    const value = field.customValue
      ? field.customValue(lodash.get(record, field.key), record)
      : (lodash.get(record, field.key) ?? "");
    const align = field.align ?? "start";
    if (field.customComponent)
      return (
        <Table.Cell
          className={classNames("td", {
            disabled: !!disabledRecord?.(record),
            [field.classNameTd as string]: !!field.classNameTd,
          })}
          colSpan={field.colSpan}
          textAlign={align}
        >
          {field.customComponent(value, record)}
        </Table.Cell>
      );

    return (
      <Table.Cell
        className={classNames("td", {
          disabled: !!disabledRecord?.(record),
          [field.classNameTd as string]: !!field.classNameTd,
        })}
        colSpan={field.colSpan}
        textAlign={align}
      >
        {value}
      </Table.Cell>
    );
  };

  const getIconOptionRecord = (icon: OptionRecord<T>["icon"]) => {
    if (!icon) return <Fragment />;

    if (typeof icon !== "string") return <>{icon}</>;

    return "";
  };

  const renderOptionsRecord = (record: T) => {
    if (!optionsRecord) return <Fragment />;
    return (
      <Table.Cell className="options__record__td" textAlign={"end"}>
        <Menu.Root>
          <Menu.Trigger
            as={DotsThreeVerticalIcon}
            className="container__meat__balls__icon"
          ></Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content className="ps-2 pe-2">
                {optionsRecord.map(
                  (
                    { label, onclick: onClickMenuItem, color, icon, show },
                    index,
                  ) => {
                    if (show && !show(record)) return null;
                    return (
                      <Menu.Item
                        value={index + ""}
                        className={`menu__item text-medium-14 cursor-pointer ${objectToClassString(
                          {
                            [`color__menu__item__${color}`]: Boolean(color),
                          },
                        )}`}
                        onClick={() => onClickMenuItem(record)}
                        key={index}
                      >
                        {getIconOptionRecord(icon)}
                        {label}
                      </Menu.Item>
                    );
                  },
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Table.Cell>
    );
  };

  return (
    <div className={classNames("container__custom__table", className)} style={style}>
      <Table.Root variant="line" style={{ tableLayout }}>
        <Table.Header>
          <Table.Row>
            {headers.map((header) => renderHeader(header))}
            {optionsRecord && (
              <Table.ColumnHeader
                colSpan={1}
                className="options__record__th"
              ></Table.ColumnHeader>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body className="position-relative">
          {isLoading && (
            <Box
              className="common-loader"
              data-state="open"
              _open={{
                animation: "fade-in 100ms ease-out",
              }}
            >
              <LeopartLoader />
            </Box>
          )}

          {data?.map((record, index) => {
            return (
              <Table.Row
                className={classNames("tr")}
                key={record?.["_id"] || index}
              >
                {headers.map((header, index) => (
                  <Fragment key={index}>
                    {renderValue(record, header, index)}
                  </Fragment>
                ))}
                {renderOptionsRecord(record)}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </div>
  );
};

export default CustomTable;
